import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Link } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Card, CardContent, Typography, Box, Stack, TextField, alpha } from '@mui/material';

import { QueryKeys } from '@frontend/features/common/types';
import { router } from '@frontend/router/router';
import { routes } from '@frontend/router/routes';
import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';
import { useCreateCaseForCall } from '@frontend/features/calls/queries';

import { cardRootStyles, cardContentStyles, cardGridStyles, labelStyles, valueStyles } from '../styles';

interface CreateCaseCardProps {
  onCancel: () => void;
  onClose: () => void;
}

const CreateCaseCard = ({ onCancel, onClose }: CreateCaseCardProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { callData } = useTwilio();

  const callId = String(callData?.callId);

  const { mutate, isLoading } = useCreateCaseForCall(callId, {
    onSuccess: ({ data: caseId }) => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.getAllCases,
      });

      onClose();
      router.navigate(routes.dashboard.caseView.replace(':id', String(caseId)));
    },
  });

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const formData = new FormData(event.currentTarget);
      const name = formData.get('name') as string;

      mutate({ name });
    },
    [mutate],
  );

  return (
    <Card
      sx={{
        ...cardRootStyles,
        border: (theme) => `1px solid ${theme.palette.secondary.main}`,
        backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.08),
      }}
    >
      <CardContent onSubmit={handleSubmit} component="form" sx={cardContentStyles}>
        <TextField
          sx={{
            '.MuiInputBase-root': {
              backgroundColor: (theme) => theme.palette.grey[800],
              color: (theme) => theme.palette.common.white,
            },
          }}
          name="name"
          size="small"
          variant="outlined"
          placeholder={t('calls.assignment.create.subject')}
        />
        <Box sx={cardGridStyles}>
          <Typography variant="body2" sx={labelStyles}>
            {t('calls.assignment.caseId')}
          </Typography>
          <Typography variant="body2" sx={valueStyles(true)}>
            #XXX
          </Typography>
          <Typography variant="body2" sx={labelStyles}>
            {t('calls.assignment.flow')}
          </Typography>
          <Typography variant="body2" sx={valueStyles(false)}>
            {t('calls.assignment.create.unassigned')}
          </Typography>
          <Typography variant="body2" sx={labelStyles}>
            {t('calls.assignment.step')}
          </Typography>
          <Typography variant="body2" sx={valueStyles(false)}>
            {t('calls.assignment.create.unassigned')}
          </Typography>
          <Typography variant="body2" sx={labelStyles}>
            {t('calls.assignment.status')}
          </Typography>
          <Typography variant="body2" sx={valueStyles(false)}>
            {t('calls.assignment.create.unassigned')}
          </Typography>
          <Typography variant="body2" sx={labelStyles}>
            {t('calls.assignment.team')}
          </Typography>
          <Typography variant="body2" sx={valueStyles(false)}>
            {t('calls.assignment.create.unassigned')}
          </Typography>
          <Typography variant="body2" sx={labelStyles}>
            {t('calls.assignment.assignedTo')}
          </Typography>
          <Typography variant="body2" sx={valueStyles(false)}>
            {t('calls.assignment.create.unassigned')}
          </Typography>
        </Box>
        <Stack flexDirection="row" gap={2}>
          <LoadingButton
            loading={isLoading}
            onClick={onCancel}
            variant="outlined"
            color="secondary"
            size="small"
            fullWidth
          >
            {t('calls.assignment.create.cancel')}
          </LoadingButton>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            type="submit"
            color="secondary"
            size="small"
            startIcon={<Link />}
            fullWidth
          >
            {t('calls.assignment.create.accept')}
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CreateCaseCard;
